/**
 * Controls Component
 * Provides user interface controls for time input, animation controls, and sharing.
 */

import { useState, useCallback } from 'react';
import type {
  TravelMode,
} from '../types';
import type { UseAnimationControllerReturn } from '../hooks/useAnimationController';
import { validateTravelTime, MIN_TRAVEL_TIME, MAX_TRAVEL_TIME } from '../utils/validation';
import { useTranslation } from '../hooks/useTranslation.tsx';
import { useAuth } from '../hooks';
import styles from './Controls.module.css';

/**
 * Props for Controls Component
 */
export interface ControlsProps {
  /** Maximum travel time (Y) */
  maxTime: number;
  /** Current animation time */
  currentTime: number;
  /** Travel mode */
  travelMode: TravelMode;
  /** Animation speed multiplier */
  animationSpeed: number;
  /** Animation state */
  animationState: 'idle' | 'playing' | 'paused' | 'completed';
  /** Whether data is loading */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Animation controller methods */
  animationController: UseAnimationControllerReturn;
  /** Callback when max time changes */
  onTimeChange: (maxTime: number) => void;
  /** Callback when travel mode changes */
  onModeChange: (mode: TravelMode) => void;
  /** Callback when animation speed changes */
  onSpeedChange?: (speed: number) => void;
  /** Origin coordinates */
  origin: { lat: number; lng: number } | null;
  /** Callback for share functionality */
  onShare?: () => void;
  /** Map style */
  mapStyle?: 'streets' | 'satellite';
  /** Callback when map style changes */
  onMapStyleChange?: (style: 'streets' | 'satellite') => void;
}

/**
 * Controls Component
 * Provides minimalist UI controls for animation and configuration.
 */
export const Controls: React.FC<ControlsProps> = ({
  maxTime,
  travelMode,
  animationSpeed,
  animationState,
  isLoading,
  error,
  animationController,
  onTimeChange,
  onModeChange,
  onSpeedChange,
  origin,
  onShare,
  mapStyle = 'streets',
  onMapStyleChange,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [timeInput, setTimeInput] = useState(maxTime.toString());
  const [timeError, setTimeError] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  /**
   * Handle time input change
   */
  const handleTimeInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setTimeInput(value);

      const numValue = parseInt(value, 10);
      if (isNaN(numValue)) {
        setTimeError(null);
        return;
      }

      if (validateTravelTime(numValue)) {
        setTimeError(null);
        onTimeChange(numValue);
      } else {
        // Error message will be translated elsewhere if needed
        setTimeError(
          `Time must be between ${MIN_TRAVEL_TIME} and ${MAX_TRAVEL_TIME} minutes`
        );
      }
    },
    [onTimeChange]
  );

  /**
   * Handle play/pause button click
   */
  const handlePlayPause = useCallback(() => {
    if (!origin) {
      return;
    }

    if (animationState === 'playing') {
      animationController.pauseAnimation();
    } else if (animationState === 'paused') {
      animationController.resumeAnimation();
    } else if (animationState === 'idle' || animationState === 'completed') {
      animationController
        .startAnimation(origin, maxTime, travelMode, animationSpeed, user?.id)
        .catch((err) => {
          console.error('Failed to start animation:', err);
        });
    }
  }, [
    origin,
    maxTime,
    travelMode,
    animationSpeed,
    animationState,
    animationController,
    user?.id,
  ]);

  /**
   * Handle restart button click
   */
  const handleRestart = useCallback(() => {
    if (!origin) {
      return;
    }
    animationController.restartAnimation();
  }, [origin, animationController]);

  /**
   * Handle speed change
   */
  const handleSpeedChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const speed = parseFloat(e.target.value);
      animationController.setSpeed(speed);
      // Also update App state if callback provided
      if (onSpeedChange) {
        onSpeedChange(speed);
      }
    },
    [animationController, onSpeedChange]
  );

  /**
   * Handle travel mode change
   */
  const handleModeChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const mode = e.target.value as TravelMode;
      onModeChange(mode);
    },
    [onModeChange]
  );

  /**
   * Handle share button click
   */
  const handleShare = useCallback(() => {
    if (onShare) {
      onShare();
    }
  }, [onShare]);

  /**
   * Handle map style change
   */
  const handleMapStyleChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const style = e.target.value as 'streets' | 'satellite';
      if (onMapStyleChange) {
        onMapStyleChange(style);
      }
    },
    [onMapStyleChange]
  );


  const canPlay = origin && !isLoading && !timeError && validateTravelTime(maxTime);
  const playPauseLabel =
    animationState === 'playing' ? t.controls.pause : t.controls.play;
  const isPlaying = animationState === 'playing';

  /**
   * Handle toggle button click
   */
  const handleToggle = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  return (
    <div className={styles.controls}>
      {error && (
        <div className={styles.error} role="alert">
          {error}
        </div>
      )}

      {/* Toggle Button - Only visible on mobile */}
      <button
        type="button"
        onClick={handleToggle}
        className={styles.toggleButton}
        aria-label={isCollapsed ? t.controls.expandControls : t.controls.collapseControls}
        aria-expanded={!isCollapsed}
      >
        <span className={`${styles.toggleIcon} ${!isCollapsed ? styles.expanded : ''}`}>▼</span>
        <span className={styles.toggleLabel}>{isCollapsed ? t.controls.showControls : t.controls.hideControls}</span>
      </button>

      <div className={`${styles.controlsPanel} ${isCollapsed ? styles.collapsed : ''}`} data-intro-step="3">
        {/* Time Input */}
        <div className={styles.controlGroup}>
          <label htmlFor="maxTime" className={styles.label}>
            {t.controls.maxTimeMinutes}
          </label>
          <input
            id="maxTime"
            type="number"
            min={MIN_TRAVEL_TIME}
            max={MAX_TRAVEL_TIME}
            value={timeInput}
            onChange={handleTimeInputChange}
            className={styles.input}
            disabled={isLoading || isPlaying}
            aria-label={t.controls.maxTimeMinutes}
            aria-invalid={timeError ? 'true' : 'false'}
            aria-describedby={timeError ? 'maxTime-error' : undefined}
          />
          {timeError && (
            <span id="maxTime-error" className={styles.errorText} role="alert">
              {timeError}
            </span>
          )}
        </div>

        {/* Travel Mode Selector */}
        <div className={styles.controlGroup}>
          <label htmlFor="travelMode" className={styles.label}>
            {t.controls.travelMode}
          </label>
          <select
            id="travelMode"
            value={travelMode}
            onChange={handleModeChange}
            className={styles.select}
            disabled={isLoading || isPlaying}
            aria-label={t.controls.travelMode}
          >
            <option value="driving">{t.travelModes.driving}</option>
            <option value="walking">{t.travelModes.walking}</option>
            <option value="cycling">{t.travelModes.cycling}</option>
            <option value="transit">{t.travelModes.transit}</option>
          </select>
        </div>

        {/* Animation Controls */}
        <div className={styles.controlGroup}>
          <div className={styles.buttonGroup}>
            <button
              type="button"
              onClick={handlePlayPause}
              disabled={!canPlay}
              className={styles.button}
              aria-label={playPauseLabel}
              data-intro-step="4"
            >
              {isLoading ? t.controls.loading : playPauseLabel}
            </button>
            <button
              type="button"
              onClick={handleRestart}
              disabled={!origin || isLoading || animationState === 'idle'}
              className={styles.button}
              aria-label={t.controls.restart}
            >
              {t.controls.restart}
            </button>
          </div>
        </div>

        {/* Speed Control */}
        <div className={styles.controlGroup}>
          <label htmlFor="speed" className={styles.label}>
            {t.controls.speed}
          </label>
          <select
            id="speed"
            value={animationSpeed.toString()}
            onChange={handleSpeedChange}
            className={styles.select}
            disabled={isLoading}
            aria-label={t.controls.speed}
          >
            <option value="0.1">{t.speed.verySlow}</option>
            <option value="0.25">{t.speed.slow}</option>
            <option value="0.5">{t.speed.half}</option>
            <option value="1">{t.speed.normal}</option>
            <option value="1.5">{t.speed.fast}</option>
            <option value="2">{t.speed.veryFast}</option>
          </select>
        </div>

        {/* Map Style Selector */}
        {onMapStyleChange && (
          <div className={styles.controlGroup}>
            <label htmlFor="mapStyle" className={styles.label}>
              {t.controls.mapStyle}
            </label>
            <select
              id="mapStyle"
              value={mapStyle}
              onChange={handleMapStyleChange}
              className={styles.select}
              aria-label={t.controls.mapStyle}
            >
              <option value="streets">{t.mapStyles.streets}</option>
              <option value="satellite">{t.mapStyles.satellite}</option>
            </select>
          </div>
        )}


        {/* Share Button */}
        {onShare && (
          <div className={styles.controlGroup}>
            <button
              type="button"
              onClick={handleShare}
              className={styles.button}
              aria-label={t.controls.share}
              data-intro-step="5"
            >
              {t.controls.share}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

